import React, { Fragment } from 'react';
import Grid from '@mui/material/Grid';
import Tile from '../components/ui/tile';
import GaugeTile from '../components/ui/tile-gauge.js';
import TileSet from '../components/ui/tileset';
import { Typography, Box, Paper, Button } from '@mui/material';
import { useContext } from 'react';
import UserProfileContext from '../context/userprofilecontext.js';
import CurrentCaseRequired from '../components/ui/current-case-required.js';
import CaseDatabaseCheck from "../components/ui/case-database-check.js";
import { useNavigate } from 'react-router-dom';

function WelcomeContent({ userProfile }) {
    const navigate = useNavigate();

    return (
        <Box sx={{ p: 4, maxWidth: 700 }}>
            <Typography variant="h4" gutterBottom>
                Welcome to Livisibly{userProfile?.currentUser?.firstName ? `, ${userProfile.currentUser.firstName}` : ''}!
            </Typography>

            {!userProfile?.hasCustomerContext && (
                <Paper sx={{ p: 3, mt: 3 }} elevation={2}>
                    <Typography variant="h6" gutterBottom>
                        Get Started
                    </Typography>
                    <Typography sx={{ mb: 2 }}>
                        To start managing cases, you'll need to set up your account. You can manage cases
                        as an individual or on behalf of an organization.  If you've been invited by someone
                        to work on their cases, please contact them to be added as a user under their case.
                    </Typography>
                    <Button
                        variant="contained"
                        onClick={() => navigate('/setup_account')}
                    >
                        Set Up My Account
                    </Button>
                </Paper>
            )}

            {userProfile?.hasCustomerContext && !userProfile?.currentCase && userProfile?.isCustomerAdmin() && (
                <Paper sx={{ p: 3, mt: 3 }} elevation={2}>
                    <Typography variant="h6" gutterBottom>
                        Create Your First Case
                    </Typography>
                    <Typography sx={{ mb: 2 }}>
                        Your account is set up and ready to go. Create a case to start uploading
                        documents and running analysis.
                    </Typography>
                    <Button
                        variant="contained"
                        onClick={() => navigate('/manage_customer_cases')}
                    >
                        Create a Case
                    </Button>
                </Paper>
            )}

            {userProfile?.hasCustomerContext && !userProfile?.currentCase && !userProfile?.isCustomerAdmin() && (
                <Paper sx={{ p: 3, mt: 3 }} elevation={2}>
                    <Typography variant="h6" gutterBottom>
                        No Cases Assigned
                    </Typography>
                    <Typography>
                        You are not currently assigned to any cases. Contact your organization
                        administrator to be assigned to a case.
                    </Typography>
                </Paper>
            )}
        </Box>
    );
}

function CaseDashboard() {
    return (
        <Grid container justifyContent="center" spacing={2}>
            <TileSet title="Document Management Insights">
                <Tile tile_key="NUM_UPLOADED_FILES" />
                <Tile tile_key="NUM_SCANNED_FILES" />
                <Tile tile_key="NUM_DUPLICATE_FILES" />
                <Tile tile_key="NUM_SCAN_ERROR_FILES" />
                <Tile tile_key="NUM_SCANNED_PAGES" />
                <Tile tile_key="NUM_TRANSACTIONS" />
            </TileSet>
            <TileSet title="Core Insights">
                <Tile tile_key="NUM_ACCOUNTS" />
                <Tile tile_key="NUM_CHECKING_ACCOUNTS" />
                <Tile tile_key="NUM_SAVINGS_ACCOUNTS" />
                <Tile tile_key="NUM_CREDITCARD_ACCOUNTS" />
                <Tile tile_key="TOTAL_INCOME" />
                <Tile tile_key="TOTAL_EXPENSES" />
                <Tile tile_key="NET_SPEND" />
            </TileSet>
            <TileSet title="Forensic Insights">
                <Tile tile_key="NUM_INTERNAL_TRANSFERS" />
                <Tile tile_key="AMT_INTERNAL_TRANSFERS" />
                <Tile tile_key="NUM_EXTERNAL_TRANSFERS" />
                <Tile tile_key="AMT_EXTERNAL_TRANSFERS" />
                <Tile tile_key="NUM_FLAGGED_TRANSACTIONS" />
                <Tile tile_key="AMT_FLAGGED_TRANSACTIONS" />
            </TileSet>
        </Grid>
    );
}

export default function Home(props) {
    const { userProfile } = useContext(UserProfileContext);

    // No customer context or no current case — show welcome/onboarding
    if (!userProfile?.currentCase) {
        return <WelcomeContent userProfile={userProfile} />;
    }

    // User has an active case — show the normal dashboard
    return (
        <CaseDatabaseCheck>
            <CurrentCaseRequired>
                <CaseDashboard />
            </CurrentCaseRequired>
        </CaseDatabaseCheck>
    );
}